import { createRouter, createWebHistory } from 'vue-router';
import MainPage from './views/MainPage.vue';
import UserProfile from './views/UserProfile.vue';
import SignupForm from './components/SignupForm.vue';
import AppLayoutWithSidebar from './layouts/AppLayoutWithSidebar.vue';
import ModuleSelectionPage from './views/ModuleSelectionPage.vue';
import CourseSelectionPage from './views/CourseSelectionPage.vue';
import { useUserStore } from './stores/userStore';
import { useCourseStore } from './stores/courseStore';
import { clearPersistedHistorySessionContext } from './composables/clearHistorySessionContext';
import AccountPendingPage from './views/AccountPendingPage.vue';
import { recoverFromStaleChunk } from './utils/chunkLoadRecovery';

const CalculatePage = () =>
  import(/* webpackChunkName: "calculate-upload" */ './views/CalculatePage.vue');
const FractionationParamsPage = () =>
  import(/* webpackChunkName: "calculate-params" */ './views/FractionationParamsPage.vue');
const AdaptiveRtStagesUploadPage = () =>
  import(/* webpackChunkName: "adaptive-rt-upload" */ './views/AdaptiveRtStagesUploadPage.vue');
const HistoryPage = () =>
  import(/* webpackChunkName: "history" */ './views/HistoryPage.vue');
const ReportSelectPage = () =>
  import(/* webpackChunkName: "report-select" */ './views/ReportSelectPage.vue');
const ReportViewPage = () =>
  import(/* webpackChunkName: "report-view" */ './views/ReportViewPage.vue');
const StructureModelSelectionPage = () =>
  import(/* webpackChunkName: "calc-standard" */ './views/StructureModelSelection.vue');
const StructureModelSelectionWithIntervals = () =>
  import(/* webpackChunkName: "calc-interval" */ './views/StructureModelSelectionWithIntervals.vue');
const StructureModelSelectionLQ = () =>
  import(/* webpackChunkName: "calc-lq" */ './views/StructureModelSelectionLQ.vue');
const StructureModelSelectionAdaptiveRt = () =>
  import(/* webpackChunkName: "calc-adaptive-rt" */ './components/StructureModelSelectionAdaptiveRt.vue');
const AdminCabinetPage = () =>
  import(/* webpackChunkName: "admin" */ './views/AdminCabinetPage.vue');

const CALCULATION_SCREEN_PATH_PREFIXES = [
  '/app/structure-model-selection',
];

const routes = [
  { path: '/', component: MainPage },
  { path: '/signup', name: 'signup', component: SignupForm },
  { path: '/profile', name: 'UserProfile', component: UserProfile, meta: { requiresAuth: true } },
  { path: '/pending', name: 'accountPending', component: AccountPendingPage, meta: { requiresAuth: true } },
  { path: '/calculate', redirect: '/app/calculate' },
  { path: '/calculate/upload', redirect: '/app/calculate/upload' },
  { path: '/structure-model-selection', redirect: '/app/structure-model-selection' },
  { path: '/structure-model-selection-interval', redirect: '/app/structure-model-selection-interval' },
  { path: '/structure-model-selection-lq', redirect: '/app/structure-model-selection-lq' },
  {
    path: '/app',
    component: AppLayoutWithSidebar,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/app/calculate' },
      { path: 'calculate/course', name: 'courseSelection', component: CourseSelectionPage },
      { path: 'calculate', name: 'calculate', component: ModuleSelectionPage },
      { path: 'calculate/params', name: 'calculateParams', component: FractionationParamsPage },
      { path: 'calculate/upload', name: 'calculateUpload', component: CalculatePage },
      { path: 'calculate/adaptive-rt/upload', name: 'adaptiveRtUpload', component: AdaptiveRtStagesUploadPage },
      { path: 'history', name: 'history', component: HistoryPage },
      { path: 'report', name: 'report', component: ReportSelectPage },
      { path: 'report/view', name: 'reportView', component: ReportViewPage },
      { path: 'admin', name: 'adminCabinet', component: AdminCabinetPage, meta: { requiresAdmin: true } },
      { path: 'structure-model-selection', component: StructureModelSelectionPage },
      { path: 'structure-model-selection-interval', component: StructureModelSelectionWithIntervals },
      { path: 'structure-model-selection-lq', component: StructureModelSelectionLQ },
      { path: 'structure-model-selection-adaptive-rt', name: 'adaptiveRtCalculation', component: StructureModelSelectionAdaptiveRt },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (to, from) => {
  const userStore = useUserStore();

  if (!userStore.sessionReady) {
    await userStore.restoreSession();
  }

  if (to.matched.some((route) => route.meta?.requiresAuth) && !userStore.user) {
    return { path: '/' };
  }

  if (userStore.user && !userStore.isApproved && to.path === '/') {
    return { name: 'accountPending' };
  }

  const needsAppAccess = to.path.startsWith('/app') && to.name !== 'adminCabinet';
  if (userStore.user && !userStore.isApproved && needsAppAccess) {
    return { name: 'accountPending' };
  }
  if (userStore.user && userStore.isApproved && to.name === 'accountPending') {
    return { path: '/app/calculate' };
  }
  if (to.matched.some((route) => route.meta?.requiresAdmin) && !userStore.isAdmin) {
    return { path: '/app/calculate' };
  }
  if (to.name === 'history' && !userStore.hasPermission('calc.history.view')) {
    return { path: '/app/calculate' };
  }
  if ((to.name === 'report' || to.name === 'reportView') && !userStore.hasPermission('calc.report.export')) {
    return { path: '/app/calculate' };
  }

  if (
    (to.name === 'history' || to.name === 'report') &&
    from.name !== to.name
  ) {
    useCourseStore().resetHistoryReportListScope();
  }

  return true;
});

function isCalculationScreenPath(path) {
  if (!path) return false;
  return CALCULATION_SCREEN_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

router.afterEach((to, from) => {
  if (isCalculationScreenPath(from.path) && !isCalculationScreenPath(to.path)) {
    clearPersistedHistorySessionContext();
  }
});

router.onError((error) => {
  recoverFromStaleChunk(error);
});

export default router;
